import { StreamStates, avSelectors } from "@minervaproject/avsubsystem";
import {
  BookmarkFilledIcon,
  BreakoutsFilledIcon,
  CaptionsIcon,
  ChatFilledIcon,
  CheckIcon,
  ControlPanelFilledIcon,
  DirectoryIcon,
  HandRaiseIcon,
  IconButton,
  PollFilledIcon,
  ResourcesFilledIcon,
  Tooltip,
  XCloseIcon,
  palette,
} from "@minervaproject/fds";
import { useEffect, useState } from "react";

import { ScreenshareControl } from "./screen_share_control";
import { ClassTimerControls } from "./timer/timer_controls";

import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import { setUserVote, toggleUserHandRaised } from "shared/reducers/classroom";
import { LocalModalContentType, RecordingStateType, SidebarPanelType } from "shared/types";
import { RoleType, VoteType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { showLocalModal, toggleSidebarPanel } from "store/reducers/local";

function SidebarControls() {
  const dispatch = useAppDispatch();
  const [isDomReady, setIsDomReady] = useState(false);
  const currentPanel = useAppSelector(localSelectors.selectSidebarPanel());
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const user = useUserProperties(currentUserId, ["is_hand_raised", "vote", "role"]);
  const { canControlClassroom, canParticipateInClass, canVisitBreakouts, isVisibleInClass } =
    useCapabilities();
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const visitingBreakoutRoom = useAppSelector(
    classroomSelectors.selectVisitingBreakoutRoomForCurrentUser(),
  );
  const hasActiveScreensharingStream =
    avSelectors.useScreenshareState(currentUserId)?.state === StreamStates.playable;
  const recordingState = useAppSelector(classroomSelectors.selectRecordingState());
  const isRecording = recordingState === RecordingStateType.Recording;

  const hasNewChatMessages = useAppSelector(localSelectors.selectHasNewChatMessages());
  // We will show the screen share control if the class is not in person and the user is a student or
  // a teacher that is not in the breakout control room unless they already have an active screenshare
  const shouldShowScreenshareControl =
    !isInPerson &&
    (canParticipateInClass ||
      (canControlClassroom &&
        (!!visitingBreakoutRoom || !activeBreakoutSessionId || hasActiveScreensharingStream)));
  const shouldShowDivider =
    (!activeBreakoutSessionId && canControlClassroom) ||
    shouldShowScreenshareControl ||
    isVisibleInClass ||
    canParticipateInClass ||
    (isRecording && !activeBreakoutSessionId);

  const getIconProps = (panel: SidebarPanelType) => {
    let color;
    let outlineColor;
    let strokeColor;
    if (panel === currentPanel) {
      color = palette.white;
    } else if (panel === SidebarPanelType.Chat && hasNewChatMessages) {
      color = palette.blueShade20;
    } else if (panel === SidebarPanelType.Captions) {
      color = palette.blackTint40;
      outlineColor = palette.black;
      strokeColor = palette.black;
    }
    return {
      width: 20,
      height: 20,
      color,
      outlineColor,
      strokeColor,
    };
  };

  const toggleVote = (vote: VoteType) => () => {
    dispatch(setUserVote({ userId: currentUserId, vote }));
  };

  useEffect(() => {
    setIsDomReady(true);
  }, []);

  return isDomReady ? (
    <div className="flex flex-column justify-center px1 bg-black h-screen">
      {canControlClassroom && (
        <Tooltip
          isGlobal
          backgroundColor={palette.blackTint20}
          place="left"
          iconComponent={
            <IconButton
              className="my1"
              onClick={() => dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Timeline }))}
              aria-label="Timeline"
            >
              <ControlPanelFilledIcon {...getIconProps(SidebarPanelType.Timeline)} />
            </IconButton>
          }
        >
          <div className="body-s">Timeline</div>
        </Tooltip>
      )}

      {user?.role === RoleType.Captioner && (
        <Tooltip
          isGlobal
          backgroundColor={palette.blackTint20}
          place="left"
          iconComponent={
            <IconButton
              className="my1"
              onClick={() => dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Captions }))}
              aria-label="Captions Input"
            >
              <CaptionsIcon {...getIconProps(SidebarPanelType.Captions)} />
            </IconButton>
          }
        >
          <div className="body-s">Captions Input</div>
        </Tooltip>
      )}

      <Tooltip
        isGlobal
        backgroundColor={palette.blackTint20}
        place="left"
        iconComponent={
          <IconButton
            className="my1"
            onClick={() => dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Chat }))}
            aria-label="Chat"
          >
            <ChatFilledIcon {...getIconProps(SidebarPanelType.Chat)} />
          </IconButton>
        }
      >
        <div className="body-s">Chat</div>
      </Tooltip>

      <Tooltip
        isGlobal
        backgroundColor={palette.blackTint20}
        place="left"
        iconComponent={
          <IconButton
            className="my1"
            onClick={() => dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Directory }))}
            aria-label="Directory"
          >
            <DirectoryIcon {...getIconProps(SidebarPanelType.Directory)} />
          </IconButton>
        }
      >
        <div className="body-s">Directory</div>
      </Tooltip>

      {(!activeBreakoutSessionId ||
        !(canControlClassroom || canVisitBreakouts) ||
        !!visitingBreakoutRoom) && (
        <Tooltip
          isGlobal
          backgroundColor={palette.blackTint20}
          place="left"
          iconComponent={
            <IconButton
              className="my1"
              onClick={() => dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Resources }))}
              aria-label="Resources"
            >
              <ResourcesFilledIcon {...getIconProps(SidebarPanelType.Resources)} />
            </IconButton>
          }
        >
          <div className="body-s">Resources</div>
        </Tooltip>
      )}

      {canControlClassroom && !activeBreakoutSessionId && (
        <Tooltip
          isGlobal
          backgroundColor={palette.blackTint20}
          place="left"
          iconComponent={
            <IconButton
              className="my1"
              onClick={() => dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Polls }))}
              aria-label="Polls"
            >
              <PollFilledIcon {...getIconProps(SidebarPanelType.Polls)} />
            </IconButton>
          }
        >
          <div className="body-s">Polls</div>
        </Tooltip>
      )}

      {(canControlClassroom || (canVisitBreakouts && !!activeBreakoutSessionId)) && (
        <Tooltip
          isGlobal
          backgroundColor={palette.blackTint20}
          place="left"
          iconComponent={
            <IconButton
              className="my1"
              onClick={() => dispatch(toggleSidebarPanel({ panel: SidebarPanelType.Breakouts }))}
              aria-label="Breakouts"
            >
              <BreakoutsFilledIcon {...getIconProps(SidebarPanelType.Breakouts)} />
            </IconButton>
          }
        >
          <div className="body-s">Breakouts</div>
        </Tooltip>
      )}

      <div
        className="flex flex-column mt2 pt2"
        style={{ borderTop: shouldShowDivider ? "1px solid var(--black-tint-40)" : "" }}
      >
        {!activeBreakoutSessionId && (
          <Tooltip
            backgroundColor={palette.blackTint20}
            place="left"
            iconComponent={<ClassTimerControls canControlClassroom={canControlClassroom} />}
          >
            Timer
          </Tooltip>
        )}

        {shouldShowScreenshareControl && <ScreenshareControl currentUserId={currentUserId} />}

        {isVisibleInClass && (
          <>
            <Tooltip
              backgroundColor={palette.blackTint20}
              place="left"
              iconComponent={
                <IconButton
                  className="my1"
                  onClick={toggleVote("yes")}
                  aria-label="Vote yes"
                  data-testid="vote-yes"
                >
                  <CheckIcon
                    width={20}
                    height={20}
                    color={user?.vote === "yes" ? "var(--green)" : "var(--black-tint-40)"}
                    data-testid="yes-icon"
                  />
                </IconButton>
              }
            >
              Vote Yes
            </Tooltip>

            <Tooltip
              backgroundColor={palette.blackTint20}
              place="left"
              iconComponent={
                <IconButton
                  className="my1"
                  onClick={toggleVote("no")}
                  aria-label="Vote no"
                  data-testid="vote-no"
                >
                  <XCloseIcon
                    width={20}
                    height={20}
                    color={user?.vote === "no" ? "var(--red)" : "var(--black-tint-40)"}
                    data-testid="no-icon"
                  />
                </IconButton>
              }
            >
              Vote No
            </Tooltip>
          </>
        )}

        {canParticipateInClass && (
          <Tooltip
            backgroundColor={palette.blackTint20}
            place="left"
            iconComponent={
              <IconButton
                className="my1"
                onClick={() => {
                  dispatch(toggleUserHandRaised({ userId: currentUserId }));
                }}
                aria-label="Raise hand"
                data-testid="raise-hand"
              >
                <HandRaiseIcon
                  width={20}
                  height={20}
                  color={
                    user?.is_hand_raised ? "var(--yellow-green-tint-20)" : "var(--black-tint-40)"
                  }
                  noCircle
                  data-testid="hand-raise-icon"
                />
              </IconButton>
            }
          >
            Raise Hand
          </Tooltip>
        )}

        {isRecording && !activeBreakoutSessionId && (
          <Tooltip
            backgroundColor={palette.blackTint20}
            place="left"
            iconComponent={
              <IconButton
                className="my1"
                onClick={() => {
                  dispatch(showLocalModal({ content: LocalModalContentType.BookmarkForm }));
                }}
                aria-label="Bookmark this moment"
              >
                <BookmarkFilledIcon width={20} height={20} data-testid="bookmark-icon" />
              </IconButton>
            }
          >
            Bookmark This Moment
          </Tooltip>
        )}
      </div>
    </div>
  ) : null;
}

export { SidebarControls };
